import React, { useEffect, useState } from 'react';
import {
  Col,
  Row,
  Input,
  InputGroup,
  InputGroupAddon,
  Button,
  Card,
  CardBody,
  Badge,
  Table,
  Spinner,
} from 'reactstrap';
import Widget from '../../components/Widget/Widget.js';
import 'eva-icons/style/eva-icons.css';
import { formatTanggalIndonesia, toSmallCaps } from '../../utils/func.js';
import { numberWithCommas } from '../../utils/func.js';
import axios from 'axios';
import { API_TIMEOUT, GET_DTSEN_CARI_INDIVIDU } from '../../utils/index.js';
import s from './CariIndividu.module.scss';

/** NIK beranda publik: 10 digit angka pertama, 6 digit terakhir disamarkan x (total tampilan 16 karakter). */
function formatNikBeranda(nik) {
  const s = String(nik || '').trim().replace(/\D/g, '');
  if (!s) return '-';
  const head = s.slice(0, 10);
  return `${head}xxxxxx`;
}

/** Nilai kolom PKH/Sembako/PBI-JK dari DTSEN untuk teks tampilan. */
function formatKemensosNilai(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') {
    if (v === 1) return 'Ya';
    if (v === 0) return 'Tidak';
    return String(v);
  }
  const t = String(v).trim();
  return t || null;
}

const CariIndividu = function ({ landingPublicMode = false }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [riwayatBantuan, setRiwayatBantuan] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [currentUser, setCurrentUser] = useState();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q || q.length < 2) {
      alert('Masukkan minimal 2 karakter (NIK atau nama)');
      return;
    }
    setLoading(true);
    setHasSearched(true);
    setData([]);
    setRiwayatBantuan({});
    axios({
      method: 'get',
      url: GET_DTSEN_CARI_INDIVIDU,
      timeout: API_TIMEOUT,
      params: { q },
    })
      .then((res) => {
        const raw = res?.data;
        setData(Array.isArray(raw?.data) ? raw.data : []);
        setRiwayatBantuan(raw?.riwayatBantuan || {});
      })
      .catch((err) => {
        setData([]);
        setRiwayatBantuan({});
        alert(err?.response?.data?.message || 'Gagal mencari data');
      })
      .finally(() => setLoading(false));
  };

  const getRiwayat = (nik) => {
    const n = (nik || '').toString().trim();
    return riwayatBantuan[n] || [];
  };

  return (
    <div>
      <Row>
        <Col>
          <Widget>
            <div className={s.heroSection}>
              <div className={s.heroIcon}>
                <i className="eva eva-search" />
              </div>
              <h2 className={s.heroTitle}>Cari Individu</h2>
              <p
                className={`${s.heroSubtitle}${landingPublicMode ? ` ${s.heroSubtitleBeranda}` : ''}`}
              >
                {landingPublicMode ? (
                  <>
                    Cari berdasarkan <strong>NIK</strong> atau <strong>Nama</strong>. Ditampilkan{' '}
                    <strong>nama</strong>, <strong>NIK</strong> (10 digit pertama + xxxxxx),{' '}
                    <strong>desa/kecamatan</strong>, <strong>hubungan keluarga</strong>, status desil, ringkasan{' '}
                    <strong>Bantuan Kementerian Sosial</strong> (PKH, Sembako, Jaminan Kesehatan bila ada), dan{' '}
                    <strong>riwayat bantuan</strong> lengkap.
                  </>
                ) : (
                  <>
                    Cari data individu berdasarkan <strong>NIK</strong> atau <strong>Nama</strong>.
                    Pencarian dilakukan ke DTSEN, jika tidak ditemukan maka ke Dukcapil. Riwayat bantuan 5 tahun
                    terakhir selalu ditampilkan.
                  </>
                )}
              </p>
              <form onSubmit={handleSearch} className={s.searchForm}>
                <InputGroup size="lg" className={s.searchInputGroup}>
                  <InputGroupAddon addonType="prepend">
                    <span className="input-group-text">
                      <i className="fa fa-id-card" aria-hidden="true" />
                    </span>
                  </InputGroupAddon>
                  <Input
                    type="text"
                    placeholder="Masukkan NIK atau nama individu..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className={s.searchInput}
                    disabled={loading}
                  />
                  <InputGroupAddon addonType="append">
                    <Button
                      type="submit"
                      color="primary"
                      disabled={loading}
                      className={s.searchBtn}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Mencari...
                        </>
                      ) : (
                        <>
                          <i className="eva eva-search mr-2" />
                          Cari
                        </>
                      )}
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </form>
            </div>
          </Widget>
        </Col>
      </Row>

      {hasSearched && (
        <Row>
          <Col>
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Memuat data...</p>
              </div>
            ) : data.length === 0 ? (
              <Widget>
                <div className="text-center py-5">
                  <i className="eva eva-close-circle-outline text-muted" style={{ fontSize: 64 }} />
                  <h5 className="mt-3">Tidak ada hasil</h5>
                  <p className="text-muted mb-0">Coba gunakan kata kunci lain atau pastikan NIK/nama benar.</p>
                </div>
              </Widget>
            ) : (
              <div className={s.resultCount}>
                Ditemukan <strong>{data.length}</strong> individu
              </div>
            )}
          </Col>
        </Row>
      )}

      {!loading && data.length > 0 && (
        <Row className="justify-content-center">
          {data.map((individu, idx) => {
            const riwayat = getRiwayat(individu.NIK);
            const pkhInfo = formatKemensosNilai(individu.bansos_pkh);
            const sembakoInfo = formatKemensosNilai(individu.bansos_sembako);
            const jkInfo = formatKemensosNilai(
              individu.pbi_jk != null ? individu.pbi_jk : individu.jaminanKesehatan
            );
            const kemensosRows = [
              { key: 'pkh', label: 'PKH', info: pkhInfo },
              { key: 'sembako', label: 'Sembako', info: sembakoInfo },
              {
                key: 'jk',
                label: 'Jaminan Kesehatan (PBI-JK)',
                info: jkInfo,
              },
            ].filter((row) => row.info != null);
            return (
              <Col key={`${individu.NIK}-${idx}`} md={12} lg={6} className="mb-4">
                <Card className={s.profileCard}>
                  <CardBody>
                    <div className={s.cardHeader}>
                      <div className={s.avatar}>
                        <i className="eva eva-person" />
                      </div>
                      <div className={s.headerInfo}>
                        <h5 className={s.nama}>{toSmallCaps(individu.nama || '-')}</h5>
                        <span className={s.nik}>
                          {landingPublicMode ? formatNikBeranda(individu.NIK) : individu.NIK || '-'}
                        </span>
                        <div className={s.badges}>
                          {individu.kategoriMampu ? (
                            <Badge color="secondary" className="mr-1">
                              Kategori Mampu (tidak termasuk Desil 1-5)
                            </Badge>
                          ) : (
                            <Badge color="info" className="mr-1">Desil {individu.desil_nasional || '-'}</Badge>
                          )}
                          {!landingPublicMode && individu.bansos_pkh && (
                            <Badge color="success" className="mr-1">PKH</Badge>
                          )}
                          {!landingPublicMode && individu.bansos_sembako && (
                            <Badge color="warning" className="mr-1">Sembako</Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {!landingPublicMode && (
                    <div className={s.infoGrid}>
                      <div className={s.infoRow}>
                        <span className={s.label}>Jenis Kelamin</span>
                        <span>{individu.jenis_kelamin || individu.dukcapil_jenis_kelamin || '-'}</span>
                      </div>
                      <div className={s.infoRow}>
                        <span className={s.label}>Tanggal Lahir</span>
                        <span>{formatTanggalIndonesia(individu.tanggal_lahir || individu.dukcapil_tanggal_lahir)}</span>
                      </div>
                      <div className={s.infoRow}>
                        <span className={s.label}>Tempat Lahir</span>
                        <span>{individu.tempat_lahir || individu.dukcapil_tempat_lahir || '-'}</span>
                      </div>
                      <div className={s.infoRow}>
                        <span className={s.label}>NOKK</span>
                        <span>{individu.NOKK || '-'}</span>
                      </div>
                      <div className={s.infoRow}>
                        <span className={s.label}>Alamat</span>
                        <span>{individu.alamat || individu.dukcapil_alamat || '-'}</span>
                      </div>
                      <div className={s.infoRow}>
                        <span className={s.label}>Desa / Kecamatan</span>
                        <span>{toSmallCaps(individu.desa || '-')}, {toSmallCaps(individu.kecamatan || '-')}</span>
                      </div>
                      <div className={s.infoRow}>
                        <span className={s.label}>Hubungan KK</span>
                        <span>{individu.hubungan_keluarga || individu.dukcapil_hubungan_kk || '-'}</span>
                      </div>
                      <div className={s.infoRow}>
                        <span className={s.label}>Pendidikan</span>
                        <span>{individu.dukcapil_pendidikan || '-'}</span>
                      </div>
                      <div className={s.infoRow}>
                        <span className={s.label}>Pekerjaan</span>
                        <span>{individu.dukcapil_pekerjaan || '-'}</span>
                      </div>
                      {individu.disabilitas && (
                        <div className={s.infoRow}>
                          <span className={s.label}>Disabilitas</span>
                          <span>{individu.disabilitas}</span>
                        </div>
                      )}
                      {(individu.pbi_jk != null && String(individu.pbi_jk).trim() !== '') ||
                      (individu.jaminanKesehatan != null &&
                        String(individu.jaminanKesehatan).trim() !== '') ? (
                        <div className={s.infoRow}>
                          <span className={s.label}>Jaminan Kesehatan (PBI-JK)</span>
                          <span>
                            {formatKemensosNilai(
                              individu.pbi_jk != null ? individu.pbi_jk : individu.jaminanKesehatan
                            ) || '—'}
                          </span>
                        </div>
                      ) : null}
                    </div>
                    )}

                    {landingPublicMode && (
                      <>
                        <div className={s.landingWilayah}>
                          <div className={s.landingWilayahRow}>
                            <span className={s.landingWilayahLabel}>Desa</span>
                            <span>{toSmallCaps(individu.desa || '—')}</span>
                          </div>
                          <div className={s.landingWilayahRow}>
                            <span className={s.landingWilayahLabel}>Kecamatan</span>
                            <span>{toSmallCaps(individu.kecamatan || '—')}</span>
                          </div>
                          <div className={`${s.landingWilayahRow} ${s.landingWilayahRowFull}`}>
                            <span className={s.landingWilayahLabel}>Hubungan Keluarga (KK)</span>
                            <span>
                              {individu.hubungan_keluarga ||
                                individu.dukcapil_hubungan_kk ||
                                '—'}
                            </span>
                          </div>
                        </div>

                        <div className={s.kemensosSection}>
                          <h6 className={s.kemensosTitle}>Bantuan Kementerian Sosial</h6>
                          {kemensosRows.length > 0 ? (
                            <Table size="sm" borderless className={s.kemensosTable}>
                              <tbody>
                                {kemensosRows.map((row) => (
                                  <tr key={row.key}>
                                    <th scope="row" className={s.kemensosProg}>
                                      {row.label}
                                    </th>
                                    <td className={s.kemensosInfo}>{row.info}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <p className={s.kemensosEmpty}>Tidak ada data program Kemensos pada DTSEN.</p>
                          )}
                        </div>

                        <p className={s.landingPublicHint}>
                          Alamat lengkap, KK, dan data Dukcapil lain tidak ditampilkan di halaman publik. Riwayat
                          bantuan di bawah tetap lengkap.
                        </p>
                      </>
                    )}

                    {riwayat.length > 0 && (
                      <div className={s.riwayatSection}>
                        <h6 className={s.riwayatTitle}>
                          <i className="eva eva-gift-outline mr-2" />
                          Riwayat Bantuan (5 Tahun Terakhir)
                        </h6>
                        <Table size="sm" className={s.riwayatTable}>
                          <thead>
                            <tr>
                              <th>Tahun</th>
                              <th>Bantuan</th>
                              <th className="text-right">Nilai</th>
                            </tr>
                          </thead>
                          <tbody>
                            {riwayat.map((r, i) => (
                              <tr key={i}>
                                <td>{r.tahun}</td>
                                <td>{r.bantuan || '-'}</td>
                                <td className="text-right">{numberWithCommas(r.nilaiUang || 0)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        <div className={s.riwayatTotal}>
                          Total: {numberWithCommas(riwayat.reduce((s, r) => s + (r.nilaiUang || 0), 0))}
                        </div>
                      </div>
                    )}

                    {riwayat.length === 0 && (
                      <div className={s.noRiwayat}>
                        <i className="eva eva-info-outline" /> Tidak ada riwayat bantuan 5 tahun terakhir
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

export default CariIndividu;
